import React, { useState, useEffect } from 'react';
import { TextField, Grid, Typography, Button, Box, Card, CardContent } from '@mui/material';
import { FixedSizeList as List } from 'react-window';
import { useSelector } from 'react-redux';
import { LoadingButton } from '@mui/lab';
import { ExpandMore } from '@mui/icons-material';
import { useNavigate } from 'react-router';
// import { PATH_DASHBOARD } from '../../../../routes/paths';
import Iconify from '../../../../components/Iconify';
import { dispatch } from '../../../../redux/store';
import {
  addToWatchListSuccess,
  getInstrumentList,
  getWatchList,
  startTradeLoading,
  stopTradeLoading,
} from '../../../../redux/slices/trades';
import useAuth from '../../../../hooks/useAuth';
import modifyScriptName from '../../../../utils/modifyScriptName';

const segements = ['NSE', 'MCX', 'COMEX', 'CRYTPO', 'FOREX'];

function AddToWatchlist() {
  const [searchText, setSearchText] = useState('');
  const [filteredScripts, setFilteredScripts] = useState([]);
  const tradeData = useSelector((state) => state?.trade);
  const { watchlist, instrumentData } = tradeData;
  const [selectedExchange, setSelectedExchange] = useState(null);
  const { user } = useAuth();
  const navigate = useNavigate();
  const scripts = instrumentData?.docs || [];
  const userSegements = segements.filter((segement) => {
    if (user[segement]) return user[segement].allow;
    return false;
  });

  useEffect(() => {
    dispatch(getInstrumentList());
    dispatch(getWatchList(false));
  }, []);

  useEffect(() => {
    filterScripts();
  }, [searchText, watchlist, instrumentData]);

  const filterScripts = () => {
    const allScripts = watchlist ? [].concat(...Object.values(watchlist)) : [];
    const filtered = searchText
      ? scripts.filter(
        (script) =>
          script.tradingsymbol.toLowerCase().includes(searchText.toLowerCase()) &&
          !allScripts.some((s) => s?._id === script._id) &&
          userSegements.includes(script?.exchange?.name)
      )
      : scripts.filter(
        (script) => !allScripts.some((s) => s?._id === script._id) && userSegements.includes(script?.exchange?.name)
      );
    setFilteredScripts(filtered);
  };

  const handleSearchChange = (event) => {
    setSearchText(event.target.value);
  };

  const handleBackclick = () => {
    // navigate(PATH_DASHBOARD.bankerRole.watchlist.root);
    navigate(-1);
  };

  const handleExchangeClick = (exchange) => {
    setSelectedExchange(selectedExchange === exchange ? null : exchange);
  };

  // const handleAddToWatchlist = (script) => {
  //   console.log('script', script);
  //   dispatch(startTradeLoading());
  //   const updatedWatchlist = JSON.parse(localStorage.getItem('watchlist')) || {};
  //   updatedWatchlist[script.exchange.name] = updatedWatchlist[script.exchange.name] || [];
  //   updatedWatchlist[script.exchange.name].push(script);
  //   localStorage.setItem('watchlist', JSON.stringify(updatedWatchlist));
  //   dispatch(addToWatchListSuccess(updatedWatchlist));
  //   const updatedFilteredScripts = filteredScripts.filter((s) => s._id !== script._id);
  //   setFilteredScripts(updatedFilteredScripts);
  // };
  const handleAddToWatchlist = (script) => {
    dispatch(startTradeLoading());

    try {
      // Get the current watchlist from local storage
      const updatedWatchlist = JSON.parse(localStorage.getItem('watchlist')) || {};

      // Initialize the exchange watchlist if it doesn't exist
      updatedWatchlist[script.exchange.name] = updatedWatchlist[script.exchange.name] || [];

      // Create a set of current script IDs for the exchange
      const scriptIds = new Set(updatedWatchlist[script.exchange.name].map((s) => s._id));

      // Add the script if its _id is not already in the set
      if (!scriptIds.has(script._id)) {
        updatedWatchlist[script.exchange.name].push(script);
        localStorage.setItem('watchlist', JSON.stringify(updatedWatchlist));
        dispatch(addToWatchListSuccess(updatedWatchlist));
      }

      // Update the filtered scripts
      const updatedFilteredScripts = filteredScripts.filter((s) => s._id !== script._id);
      setFilteredScripts(updatedFilteredScripts);
    } catch (error) {
      console.error('Error adding to watchlist:', error);
    } finally {
      // Stop loading regardless of success or failure
      dispatch(stopTradeLoading());
    }
  };

  const renderRow = ({ index, style }) => {
    const script = filteredScripts[index];
    return (
      <Grid
        container
        style={style}
        key={script._id}
        alignItems="center"
        spacing={2}
        sx={{ cursor: 'pointer', ':hover': { backgroundColor: 'action.selected' } }}
        className="list-item"
        onClick={() => handleAddToWatchlist(script)}
      >
        <Grid item xs={8}>
          <Typography>{modifyScriptName(script.tradingsymbol, script.expiry, script)}</Typography>
        </Grid>
        <Grid item xs={2}>
          <Typography align="center">{script.exchange.name}</Typography>
        </Grid>
        <Grid item xs={2}>
          <Box textAlign="right">
            <LoadingButton variant="contained" onClick={() => handleAddToWatchlist(script)}>
              Add
            </LoadingButton>
          </Box>
        </Grid>
      </Grid>
    );
  };

  return (
    <Box>
      <Button
        startIcon={<Iconify icon={'eva:arrow-back-outline'} />}
        variant="outlined"
        sx={{ mb: 2, ml: 1 }}
        onClick={handleBackclick}
      >
        Back
      </Button>
      <Grid container spacing={2} sx={{ px: 1 }}>
        <Grid item xs={12}>
          <TextField fullWidth label="Search Scripts" value={searchText} onChange={handleSearchChange} />
        </Grid>
        <Grid item xs={12}>
          {tradeData.isLoading && (
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <LoadingButton
                loading
                size="large"
                sx={{
                  '.MuiCircularProgress-root': {
                    width: '50px !important',
                    height: '50px !important',
                    color: 'primary.main',
                  },
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  color: 'primary.main',
                }}
              />
            </Box>
          )}
          {searchText ? (
            <List height={400} itemCount={filteredScripts.length} itemSize={50} width="100%">
              {renderRow}
            </List>
          ) : (
            <>
              {Object.entries(
                filteredScripts.reduce((acc, script) => {
                  const exchange = script.exchange.name || 'Unknown';
                  if (!acc[exchange]) {
                    acc[exchange] = [];
                  }
                  acc[exchange].push(script);
                  return acc;
                }, {})
              ).map(([exchange, scripts]) => (
                <React.Fragment key={exchange}>
                  <Card
                    sx={{ cursor: 'pointer', padding: 1, marginBottom: 2 }}
                    onClick={() => handleExchangeClick(exchange)}
                  >
                    <CardContent sx={{ display: 'flex', alignItems: 'center' }}>
                      <Typography variant="h6">{exchange}</Typography>
                      <ExpandMore sx={{ marginLeft: 'auto' }} />
                    </CardContent>
                  </Card>
                  {selectedExchange === exchange && (
                    <List height={400} itemCount={scripts.length} itemSize={50} width="100%">
                      {({ index, style }) => {
                        const script = scripts[index];
                        return (
                          <Grid
                            container
                            style={style}
                            key={script._id}
                            alignItems="center"
                            spacing={2}
                            sx={{ cursor: 'pointer', ':hover': { backgroundColor: 'action.selected' } }}
                            className="list-item"
                            onClick={() => handleAddToWatchlist(script)}
                          >
                            <Grid item xs={8}>
                              <Typography>{modifyScriptName(script.tradingsymbol, script.expiry, script)}</Typography>
                            </Grid>
                            <Grid item xs={2}>
                              <Typography align="center">{script.exchange.name}</Typography>
                            </Grid>
                            <Grid item xs={2}>
                              <Box textAlign="right">
                                <LoadingButton variant="contained">Add</LoadingButton>
                              </Box>
                            </Grid>
                          </Grid>
                        );
                      }}
                    </List>
                  )}
                </React.Fragment>
              ))}
            </>
          )}
        </Grid>
      </Grid>
    </Box>
  );
}

export default AddToWatchlist;
